// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the corner assistant's pop-ups. Keeps the suggestions due for me (GET
// /konvo/corner/pending once the app has settled after start, every 2 min, on focus (not
// within 10 s of the last load), when the gateway reconnects, plus
// KONVO_CORNER_UPDATE pushes),
// puts up one at a time (meeting prep first, then the oldest), holds it while I am busy
// (reported once as 'held', dropped after 10 min), closes it after 20 s leaving a dot until
// it expires, and reports every outcome to the server and to activity tracking: shown,
// seen, hovered, clicked, closed and, five minutes after an ignore, what I did next.
// The rules themselves are pure and tested in CornerQueue.ts.

import {Routes} from '@app/app/Routes';
import MobileRailPanelState from '@app/features/app/components/layout/mobile_rail/MobileRailPanelState';
import {MOBILE_KEYBOARD_OPEN_CLASS} from '@app/features/app/hooks/useIosKeyboardAccessory';
import Channels from '@app/features/channel/state/Channels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import Activity, {activityDevice} from '@app/features/konvo_activity/state/Activity';
import AskKonvo from '@app/features/konvo_ask/state/AskKonvo';
import AskChangeSignal from '@app/features/konvo_corner/state/AskChangeSignal';
import {busyReason, watchTyping} from '@app/features/konvo_corner/state/CornerBusy';
import {
	appearanceKey,
	type CornerTimings,
	canDrawCard,
	classifyFollowed,
	DEFAULT_TIMINGS,
	decideHold,
	dotVisible,
	type HoldState,
	isFinalOutcome,
	isFinalStatus,
	isWaiting,
	pageForPath,
	pickNext,
	readTimings,
	resumeAutoCloseMs,
} from '@app/features/konvo_corner/state/CornerQueue';
import CornerSettings from '@app/features/konvo_corner/state/CornerSettings';
import ReadyList from '@app/features/konvo_corner/state/ReadyList';
import KonvoPanel from '@app/features/konvo_panel/state/KonvoPanel';
import HomeContext from '@app/features/konvo_today/state/HomeContext';
import HomePeek from '@app/features/konvo_today/state/HomePeek';
import WaitingStore from '@app/features/konvo_waiting/state/WaitingStore';
import Navigation from '@app/features/navigation/state/Navigation';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {CoalescedLoad} from '@app/features/platform/transport/CoalescedLoad';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {afterStartup} from '@app/features/platform/utils/StartupScheduler';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import Modal from '@app/features/ui/state/Modal';
import {VisiblePoll} from '@app/features/ui/utils/VisiblePoll';
import Window from '@app/features/window/state/Window';
import {
	type AssistantStage,
	type CornerOutcome,
	type CornerPendingResponse,
	type CornerSuggestion,
	CornerSuggestionSchema,
	type DeviceKind,
	type Starter,
} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';
import {makeAutoObservable, observable, reaction, runInAction} from 'mobx';

const logger = new Logger('Corner');

export const CORNER_PENDING_ENDPOINT = '/konvo/corner/pending';
export const cornerOutcomeEndpoint = (id: string) => `/konvo/corner/suggestions/${encodeURIComponent(id)}/outcome`;
export const cornerSuggestionEndpoint = (id: string) => `/konvo/corner/suggestions/${encodeURIComponent(id)}`;
/** localStorage key for shortened timings in tests (JSON, see CornerQueue.readTimings). */
export const CORNER_TIMINGS_KEY = 'konvo_corner_timings';
/** Query parameter a push notification opens the app with: the suggestion to run. */
export const CORNER_DEEP_LINK_PARAM = 'konvo_panel';

/** How often the queue is looked at again (holds, due times, expiry of the dot). */
const TICK_MS = 5_000;
/** Focus and reconnects within this long of the last load reuse it. */
const LOAD_GAP_MS = 10_000;
/** A push deep link that could not be run (the load failed) is forgotten after this long. */
const DEEP_LINK_TTL_MS = 30_000;

export type CornerMenuChoice = 'snooze' | 'not_this' | 'stop' | 'settings';
type CloseHow = 'x' | 'timeout' | 'navigated' | 'later' | 'main';

interface Visible {
	suggestion: CornerSuggestion;
	shownAt: number;
	/** Reopened from the dot: no auto-close and no second 'shown'. */
	reopened: boolean;
}

interface Follower {
	id: string;
	kind: CornerSuggestion['kind'];
	starter: Starter | undefined;
	startedAt: number;
	panelStarters: Array<Starter>;
	askedKonvo: boolean;
	areas: Set<string>;
	awayMs: number;
	awaySince: number | null;
	timer: ReturnType<typeof setTimeout>;
}

/** In the Konvo (Assistant) DM you are already talking to the assistant: the corner hides there. */
export function inKonvoDm(pathname: string): boolean {
	const match = /^\/channels\/@me\/(\d+)/.exec(pathname);
	if (!match) return false;
	const channel = Channels.getChannel(match[1]);
	return channel ? ChannelUtils.isSystemDmChannel(channel) : false;
}

function readKeyboardOpen(): boolean {
	return typeof document !== 'undefined' && document.documentElement.classList.contains(MOBILE_KEYBOARD_OPEN_CLASS);
}

function currentDevice(): DeviceKind {
	return activityDevice();
}

function readStoredTimings(): CornerTimings {
	try {
		return readTimings(getProtectedLocalStorage()?.getItem(CORNER_TIMINGS_KEY));
	} catch {
		return DEFAULT_TIMINGS;
	}
}

let gutterIds = 0;

export class CornerStore {
	suggestions: Array<CornerSuggestion> = [];
	visible: Visible | null = null;
	/** A pop-up that closed on its own: its dot stays on the icon until it expires. */
	dot: {suggestion: CornerSuggestion; expiresAt: number; setAt: number} | null = null;
	stage: AssistantStage | null = null;
	usedToday = 0;
	limit = 0;
	/** Ticks so time-based getters (the dot) re-evaluate. */
	now = Date.now();
	/** Phone: bottom bars on screen (the rail registers itself while mounted). */
	phoneBars = 0;
	/** Phone: the software keyboard is up (the bar hides under it). */
	keyboardOpen = false;
	/**
	 * Desktop: the page keeps a gutter free for the icon (the Agents page), so the icon stays solid
	 * there instead of fading over content, with the page's own tooltip (null: the usual one).
	 */
	gutter: {id: number; tooltip: string | null} | null = null;
	/**
	 * Local cards up right now (the "I'll remember that" card, "Am I getting in your way?"): no
	 * suggestion goes up over them.
	 */
	localCards = new Set<string>();
	/**
	 * Desktop: the page's own card sits in the corner's spot (the mail reader's Konvo card): the
	 * corner draws no pop-up of its own over or under it, and puts none up until it closes.
	 */
	pageCard = false;
	/**
	 * Desktop: the page has something from Konvo about what is open (the mail reader's summary of
	 * the email). The icon carries a dot with the page's line as its tooltip, and a click opens the
	 * page's card instead of the panel.
	 */
	pagePeek: {id: number; label: string; open: () => void} | null = null;

	private handled = new Set<string>();
	/** Suggestions this tab already ended for good (used, dismissed): nothing more is posted for them. */
	private finals = new Set<string>();
	/** Used in place on this tab and still on screen (usedInPlace): server updates do not take it down. */
	private usedHere = new Set<string>();
	/** Suggestions this tab already reported as timed out. */
	private timedOut = new Set<string>();
	private holds = new Map<string, HoldState>();
	private hovered = new Set<string>();
	private followers = new Map<string, Follower>();
	private timers: Array<() => void> = [];
	private autoClose: ReturnType<typeof setTimeout> | null = null;
	/** When the running auto-close fires; null while paused. */
	private autoCloseDeadline: number | null = null;
	/** What was left on the clock when it paused for a hidden stretch; null = start over. */
	private autoCloseRemaining: number | null = null;
	/** The pointer is over the card. */
	private pointerOver = false;
	/** The card's "…" menu (or the phone sheet) is open. */
	private menuIsOpen = false;
	/** The visible card could not be drawn since then (its time on screen stops counting). */
	private hiddenSince: number | null = null;
	private timings: CornerTimings = DEFAULT_TIMINGS;
	private started = false;
	private deepLinkId: string | null = null;
	private deepLinkAt = 0;
	/** Bumped on reset: replies to requests made for the previous person are dropped. */
	private generation = 0;
	/** One pending-list request at a time; focus, reconnects and the poll share it. */
	private loads = new CoalescedLoad(LOAD_GAP_MS);

	constructor() {
		makeAutoObservable<
			this,
			| 'handled'
			| 'finals'
			| 'usedHere'
			| 'timedOut'
			| 'holds'
			| 'hovered'
			| 'followers'
			| 'timers'
			| 'autoClose'
			| 'autoCloseDeadline'
			| 'autoCloseRemaining'
			| 'pointerOver'
			| 'menuIsOpen'
			| 'hiddenSince'
			| 'timings'
			| 'started'
			| 'deepLinkId'
			| 'deepLinkAt'
			| 'generation'
			| 'loads'
		>(
			this,
			{
				pagePeek: observable.ref,
				handled: false,
				finals: false,
				usedHere: false,
				timedOut: false,
				holds: false,
				hovered: false,
				followers: false,
				timers: false,
				autoClose: false,
				autoCloseDeadline: false,
				autoCloseRemaining: false,
				pointerOver: false,
				menuIsOpen: false,
				hiddenSince: false,
				timings: false,
				started: false,
				deepLinkId: false,
				deepLinkAt: false,
				generation: false,
				loads: false,
			},
			{autoBind: true},
		);
	}

	/** A suggestion is waiting: the icon (or the phone Ask button) shows a dot. */
	get hasDot(): boolean {
		return dotVisible(this.dot, this.now);
	}

	/**
	 * Desktop: the whole corner (icon and pop-up) steps aside for the panel, a modal, Home's
	 * pop-up (its panel fills the corner), the Konvo DM and Home while it is the Konvo conversation.
	 */
	get cornerHidden(): boolean {
		return (
			KonvoPanel.isOpen ||
			Modal.hasModalOpen() ||
			HomePeek.isOpen ||
			HomeContext.conversationOpen ||
			inKonvoDm(Navigation.pathname)
		);
	}

	/**
	 * Desktop: the icon belongs on this page, so message lists keep clear of it. A modal or the panel
	 * hides it only for a moment and does not count, or the messages would jump behind them.
	 */
	get holdsCorner(): boolean {
		return !inKonvoDm(Navigation.pathname);
	}

	/**
	 * A pop-up can be seen right now. When it cannot, nothing new goes up (and no 'shown' is
	 * reported) and the one already up keeps its place with its clock paused.
	 */
	get canDraw(): boolean {
		return canDrawCard({
			phone: MobileLayout.enabled,
			panelOpen: KonvoPanel.isOpen,
			modalOpen: Modal.hasModalOpen(),
			inKonvoDm: inKonvoDm(Navigation.pathname),
			phoneBar: this.phoneBars > 0,
			railPanelOpen: MobileRailPanelState.open != null,
			keyboardOpen: this.keyboardOpen,
		});
	}

	/** The pop-up to draw now (null while it cannot be seen, see canDraw). */
	get card(): CornerSuggestion | null {
		if (!this.visible || !this.canDraw) return null;
		return this.visible.suggestion;
	}

	/** Phone: the bottom bar is on screen while this is registered (MobileRail calls it on mount). */
	attachPhoneBar(): () => void {
		this.phoneBars += 1;
		let attached = true;
		return () => {
			if (!attached) return;
			attached = false;
			this.detachPhoneBar();
		};
	}

	/** A local card is up (see localCards). */
	get localCard(): boolean {
		return this.localCards.size > 0;
	}

	/** A local card went up or came down (the last one down: put up a waiting suggestion). */
	setLocalCard(up: boolean, source = 'memory'): void {
		if (this.localCards.has(source) === up) return;
		const next = new Set(this.localCards);
		if (up) next.add(source);
		else next.delete(source);
		this.localCards = next;
		if (!this.localCard) this.evaluate();
	}

	/** The page's own card opened or closed (closed: put up a waiting suggestion). */
	setPageCard(open: boolean): void {
		if (this.pageCard === open) return;
		this.pageCard = open;
		if (!open) this.evaluate();
	}

	/** Desktop: the page leaves the icon its own gutter while this is registered (useCornerGutter). */
	attachGutter(tooltip: string | null): () => void {
		const id = ++gutterIds;
		this.gutter = {id, tooltip};
		return () => this.detachGutter(id);
	}

	attachPagePeek(label: string, open: () => void): () => void {
		const id = ++gutterIds;
		this.pagePeek = {id, label, open};
		return () => this.detachPagePeek(id);
	}

	private detachPagePeek(id: number): void {
		if (this.pagePeek?.id === id) this.pagePeek = null;
	}

	private detachGutter(id: number): void {
		if (this.gutter?.id === id) this.gutter = null;
	}

	private detachPhoneBar(): void {
		this.phoneBars = Math.max(0, this.phoneBars - 1);
	}

	private setKeyboardOpen(open: boolean): void {
		this.keyboardOpen = open;
	}

	get page(): string {
		return pageForPath(Navigation.pathname);
	}

	/** Start once the app is signed in (the corner icon or the phone rail mounts it). */
	start(): () => void {
		if (this.started) return () => {};
		this.started = true;
		this.timings = readStoredTimings();
		this.readDeepLink();
		const stopTyping = watchTyping();
		const poll = new VisiblePoll(this.timings.pollMs, () => this.load(), true, this.timings.pollMs);
		const tick = new VisiblePoll(TICK_MS, () => this.tick(), true, TICK_MS);
		poll.start();
		tick.start();
		const stopFocus = reaction(
			() => Window.focused && Window.visible,
			(active) => {
				if (active) void this.load();
				else this.evaluate();
			},
		);
		// Pushes sent while the gateway was reconnecting are lost: fetch again once it is back
		// (the first connect after start is covered by the startup load below).
		const stopGateway = reaction(
			() => GatewayConnection.isReady && GatewayConnection.isConnected,
			(ready) => {
				if (ready && Window.visible && this.loads.lastLoadedAt > Number.NEGATIVE_INFINITY) void this.load(true);
			},
		);
		const stopFollow = this.watchFollowSignals();
		const stopDeepLink = reaction(
			() => Navigation.search,
			() => {
				if (this.readDeepLink()) void this.load(true);
			},
		);
		// Covered (panel, modal, Konvo DM, phone Ask/More panel, keyboard) or back: pause or
		// resume the clock of the card that is up, and put up a waiting one once it can be seen.
		const stopDraw = reaction(
			() => this.canDraw,
			(can) => {
				this.syncAutoClose();
				if (can) this.evaluate();
			},
		);
		this.setKeyboardOpen(readKeyboardOpen());
		const keyboard = new MutationObserver(() => {
			const open = readKeyboardOpen();
			if (open !== this.keyboardOpen) this.setKeyboardOpen(open);
		});
		keyboard.observe(document.documentElement, {attributes: true, attributeFilter: ['class']});
		// The "From Konvo" list loads and refreshes with the corner; a pop-up waits while it is open.
		const stopList = ReadyList.start();
		const stopListOpen = reaction(
			() => ReadyList.isOpen,
			(open) => {
				if (!open) this.evaluate();
			},
		);
		this.timers = [
			stopList,
			stopListOpen,
			stopTyping,
			() => poll.stop(),
			() => tick.stop(),
			stopFocus,
			stopGateway,
			stopFollow,
			stopDeepLink,
			stopDraw,
			() => keyboard.disconnect(),
		];
		// A push deep link is run at once; otherwise the queue loads once the app has settled (a
		// pop-up is never the first thing on screen, and the first seconds belong to the page).
		if (this.deepLinkId) void this.load(true);
		else
			this.timers.push(
				afterStartup(
					() => {
						if (Window.visible) void this.load();
					},
					{delayMs: 2000, jitterMs: 4000},
				),
			);
		return () => this.stop();
	}

	/** Stop everything and forget the signed-in person's suggestions (sign-out, account switch). */
	stop(): void {
		for (const stop of this.timers) stop();
		this.timers = [];
		this.started = false;
		this.reset();
	}

	/**
	 * Forget everything about the current person: queue, the card that is up, the dot, what was
	 * dealt with, holds, stage and today's count. Replies still on their way are dropped.
	 */
	reset(): void {
		this.generation += 1;
		AskChangeSignal.reset();
		this.clearAutoClose();
		this.autoCloseRemaining = null;
		this.pointerOver = false;
		this.menuIsOpen = false;
		this.hiddenSince = null;
		for (const follower of this.followers.values()) clearTimeout(follower.timer);
		this.followers.clear();
		this.suggestions = [];
		this.visible = null;
		this.dot = null;
		this.stage = null;
		this.usedToday = 0;
		this.limit = 0;
		this.handled.clear();
		this.finals.clear();
		this.usedHere.clear();
		this.timedOut.clear();
		this.holds.clear();
		this.hovered.clear();
		this.deepLinkId = null;
		this.deepLinkAt = 0;
		this.loads.reset();
	}

	/** The pending list; focus within LOAD_GAP_MS of the last load reuses it unless `force`. */
	load(force = false): Promise<void> {
		return this.loads.run(() => this.fetchPending(), {force});
	}

	private async fetchPending(): Promise<void> {
		const generation = this.generation;
		const requestedAt = Date.now();
		try {
			const response = await http.get<CornerPendingResponse>(CORNER_PENDING_ENDPOINT);
			const body = response.body;
			if (!body || generation !== this.generation || !this.started) return;
			AskChangeSignal.report(body, requestedAt);
			const suggestions = (Array.isArray(body.suggestions) ? body.suggestions : []).flatMap((raw) => {
				const parsed = CornerSuggestionSchema.safeParse(raw);
				return parsed.success ? [parsed.data] : [];
			});
			runInAction(() => {
				this.suggestions = suggestions;
				this.stage = body.stage ?? null;
				this.usedToday = body.used_today ?? 0;
				this.limit = body.limit ?? 0;
			});
			this.runDeepLink();
			this.evaluate();
		} catch (error) {
			logger.warn('Could not load corner suggestions', error);
		}
	}

	/** Gateway KONVO_CORNER_UPDATE: a new or changed suggestion, or one taken away. */
	applyUpdate(update: {suggestion?: CornerSuggestion; removed?: string}): void {
		if (update.removed) {
			const id = update.removed;
			this.suggestions = this.suggestions.filter((s) => s.id !== id);
			// A card used in place (the wrap-up's Start) stays up to show what happened.
			if (this.visible?.suggestion.id === id && !this.usedHere.has(id)) this.hide();
			this.clearDotFor(id);
		}
		const next = update.suggestion;
		if (next) {
			// A pushed suggestion that is new to this client may be shown even if an older copy was handled.
			const others = this.suggestions.filter((s) => s.id !== next.id);
			this.suggestions = [...others, next];
			if (this.visible?.suggestion.id === next.id) {
				// Dealt with on another device (used, dismissed, skipped): take it down here too.
				const live = isWaiting(next.status) || next.status === 'shown' || this.usedHere.has(next.id);
				if (live) this.visible = {...this.visible, suggestion: next};
				else this.hide();
			}
			if (this.dot?.suggestion.id === next.id && next.status !== 'timed_out' && next.status !== 'shown')
				this.dot = null;
			// Closed by itself on another tab or device: keep a dot here too until it expires.
			if (next.status === 'timed_out' && !this.dot) {
				const expiresAt = Date.parse(next.expires_at);
				if (Number.isFinite(expiresAt) && expiresAt > Date.now())
					this.dot = {suggestion: next, expiresAt, setAt: Date.now()};
			}
		}
		// The list changes with every push (new, shown, used elsewhere): load it once the burst is over.
		ReadyList.refreshSoon();
		this.evaluate();
	}

	private tick(): void {
		this.now = Date.now();
		if (this.dot && !dotVisible(this.dot, this.now)) this.dot = null;
		this.evaluate();
	}

	/**
	 * Look at the queue: show the next one when free, hold it when busy, drop it when too late.
	 * Nothing happens while a pop-up cannot be seen (canDraw): it stays queued until it can.
	 */
	evaluate(): void {
		if (!this.started) return;
		if (this.visible || this.localCard || this.pageCard) return;
		// The list is open: what is new shows there (it refreshes on the push), not as a pop-up over it.
		if (ReadyList.isOpen) return;
		if (!CornerSettings.enabled && CornerSettings.settings) return;
		// "Only in the list": nothing pops up here either (the server holds them too).
		if (CornerSettings.listOnly) return;
		if (!this.canDraw) return;
		const now = Date.now();
		const next = pickNext(this.suggestions, now, this.handled);
		if (!next) return;
		const busy = busyReason(now);
		const hold = this.holds.get(next.id);
		const decision = decideHold(next, busy, hold, now, this.timings.holdMs);
		if (decision.action === 'drop') {
			this.handled.add(appearanceKey(next));
			this.holds.delete(next.id);
			this.evaluate();
			return;
		}
		if (decision.action === 'hold') {
			this.holds.set(next.id, {since: hold?.since ?? now, reported: true});
			if (decision.report) {
				void this.postOutcome(next.id, {status: 'held'});
				Activity.track('suggestion.held_back', {suggestion_id: next.id, kind: next.kind, reason: decision.reason});
			}
			return;
		}
		this.show(next);
	}

	private show(suggestion: CornerSuggestion): void {
		const page = this.page;
		const device = currentDevice();
		this.holds.delete(suggestion.id);
		this.startVisible({suggestion, shownAt: Date.now(), reopened: false});
		this.clearDotFor(suggestion.id);
		void this.postOutcome(suggestion.id, {status: 'shown', page, device});
		Activity.track('suggestion.shown', {suggestion_id: suggestion.id, kind: suggestion.kind, page, device});
	}

	/** Put a card up with a fresh clock (hover and menu state belong to the previous card). */
	private startVisible(visible: Visible): void {
		this.clearAutoClose();
		this.autoCloseRemaining = null;
		this.pointerOver = false;
		this.menuIsOpen = false;
		this.hiddenSince = null;
		this.visible = visible;
		this.syncAutoClose();
	}

	private clearAutoClose(): void {
		if (this.autoClose) clearTimeout(this.autoClose);
		this.autoClose = null;
		this.autoCloseDeadline = null;
	}

	/**
	 * Run or pause the 20 s auto-close. It pauses while the pointer is over the card, while its
	 * menu is open and while it cannot be seen; after a hidden stretch it resumes with the time
	 * that was left, after hover or the menu it starts over. Reopened cards never close by themselves.
	 */
	private syncAutoClose(): void {
		const visible = this.visible;
		const now = Date.now();
		const drawable = this.canDraw;
		if (visible && !drawable) {
			if (this.hiddenSince == null) this.hiddenSince = now;
		} else if (this.hiddenSince != null) {
			// Time spent hidden does not count as time on screen.
			if (visible) this.visible = {...visible, shownAt: visible.shownAt + (now - this.hiddenSince)};
			this.hiddenSince = null;
		}
		const paused = !visible || visible.reopened || this.pointerOver || this.menuIsOpen || !drawable;
		if (paused) {
			if (this.autoClose && this.autoCloseDeadline != null) {
				this.autoCloseRemaining = Math.max(0, this.autoCloseDeadline - now);
			}
			this.clearAutoClose();
			return;
		}
		if (this.autoClose) return;
		const wait = resumeAutoCloseMs(this.autoCloseRemaining, this.timings.autoCloseMs);
		this.autoCloseRemaining = null;
		this.autoCloseDeadline = now + wait;
		this.autoClose = setTimeout(() => this.timeout(), wait);
	}

	/** The pointer is over the card: count it once, and do not close it under the pointer. */
	hover(entering: boolean): void {
		const visible = this.visible;
		if (!visible) return;
		this.pointerOver = entering;
		if (entering) {
			if (!this.hovered.has(visible.suggestion.id)) {
				this.hovered.add(visible.suggestion.id);
				Activity.track('suggestion.hovered', {suggestion_id: visible.suggestion.id, kind: visible.suggestion.kind});
			}
		} else {
			// Left the card: a fresh 20 s from now.
			this.autoCloseRemaining = null;
		}
		this.syncAutoClose();
	}

	/** The card's "…" menu opened or closed: never close the card while its menu is open. */
	menuOpened(open: boolean): void {
		if (this.menuIsOpen === open) return;
		this.menuIsOpen = open;
		if (!open) this.autoCloseRemaining = null;
		this.syncAutoClose();
	}

	/** Take the card down and report how long it was up. */
	private close(how: CloseHow): Visible | null {
		const visible = this.visible;
		if (!visible) return null;
		this.takeDown();
		const {suggestion} = visible;
		this.handled.add(appearanceKey(suggestion));
		const seconds = Math.round((Date.now() - visible.shownAt) / 100) / 10;
		if (seconds * 1000 >= this.timings.seenMs) {
			Activity.track('suggestion.seen', {suggestion_id: suggestion.id, kind: suggestion.kind, seconds});
		}
		Activity.track('suggestion.closed', {suggestion_id: suggestion.id, kind: suggestion.kind, how});
		return visible;
	}

	private hide(): void {
		this.takeDown();
	}

	private takeDown(): void {
		this.clearAutoClose();
		this.autoCloseRemaining = null;
		this.pointerOver = false;
		this.menuIsOpen = false;
		this.hiddenSince = null;
		this.visible = null;
	}

	/** Clear the dot only if it belongs to this suggestion (another one's dot stays). */
	private clearDotFor(id: string): void {
		if (this.dot?.suggestion.id === id) this.dot = null;
	}

	private clicked(suggestion: CornerSuggestion, target: string, via?: 'push'): void {
		Activity.track('suggestion.clicked', {
			suggestion_id: suggestion.id,
			kind: suggestion.kind,
			target,
			...(via ? {via} : {}),
		});
	}

	/**
	 * Closed by itself after 20 s: it folds into the "From Konvo" list (the server treats
	 * 'timed_out' as folded), so the icon shows the list's count and a one-line peek for a minute.
	 * The dot stays as the fallback for a server without the list.
	 */
	timeout(): void {
		const visible = this.close('timeout');
		if (!visible) return;
		const {suggestion} = visible;
		if (!visible.reopened) {
			void this.postOutcome(suggestion.id, {status: 'timed_out', page: this.page});
			ReadyList.fold(suggestion);
		}
		const expiresAt = Date.parse(suggestion.expires_at);
		if (Number.isFinite(expiresAt) && expiresAt > Date.now()) this.dot = {suggestion, expiresAt, setAt: Date.now()};
		this.follow(suggestion);
		this.evaluate();
	}

	/**
	 * A tap on the icon (or the phone Ask button) while its dot shows: bring the pop-up back.
	 * Does nothing while another pop-up is up (that one owns the corner until it closes).
	 */
	reopen(): boolean {
		if (this.visible) return false;
		const dot = this.dot;
		if (!dot || !dotVisible(dot, Date.now())) return false;
		this.dot = null;
		if (this.finals.has(dot.suggestion.id)) return false;
		this.handled.delete(appearanceKey(dot.suggestion));
		this.startVisible({suggestion: dot.suggestion, shownAt: Date.now(), reopened: true});
		return true;
	}

	/** The main button: run it (the panel, the connect step or the brief). */
	main(): void {
		const visible = this.close('main');
		if (!visible) return;
		this.clearDotFor(visible.suggestion.id);
		this.clicked(visible.suggestion, 'main');
		this.runAction(visible.suggestion);
	}

	later(): void {
		const visible = this.close('later');
		if (!visible) return;
		this.clicked(visible.suggestion, 'later');
		void this.postOutcome(visible.suggestion.id, {status: 'later', page: this.page});
		this.evaluate();
	}

	dismiss(): void {
		const visible = this.close('x');
		if (!visible) return;
		this.clearDotFor(visible.suggestion.id);
		this.clicked(visible.suggestion, 'x');
		void this.postOutcome(visible.suggestion.id, {status: 'dismissed', page: this.page});
		this.follow(visible.suggestion);
		this.evaluate();
	}

	/** Meeting prep asking once after three ignores: "Keep them" (used) closes it without a run. */
	keep(): void {
		const visible = this.close('main');
		if (!visible) return;
		this.clearDotFor(visible.suggestion.id);
		this.clicked(visible.suggestion, 'keep');
		void this.postOutcome(visible.suggestion.id, {status: 'used', page: this.page});
		this.evaluate();
	}

	/**
	 * A card that acts in place (agent_task: Link, Both, Other task) was used: it closes as used,
	 * with what was pressed, without opening the panel.
	 */
	answered(target: string): void {
		const visible = this.close('main');
		if (!visible) return;
		this.clearDotFor(visible.suggestion.id);
		this.clicked(visible.suggestion, target);
		void this.postOutcome(visible.suggestion.id, {status: 'used', page: this.page, device: currentDevice()});
		this.evaluate();
	}

	/**
	 * A card that acts in place and stays up to show what happened (the wrap-up's Start, Not tonight):
	 * reported used once; the card stays until it is closed or times out.
	 */
	usedInPlace(target: string): void {
		const visible = this.visible;
		if (!visible) return;
		this.clearDotFor(visible.suggestion.id);
		this.clicked(visible.suggestion, target);
		this.usedHere.add(visible.suggestion.id);
		void this.postOutcome(visible.suggestion.id, {status: 'used', page: this.page, device: currentDevice()});
	}

	/** "Not now" on the connect invitation: a dismissal (the server stops after three). */
	notNow(): void {
		const visible = this.close('later');
		if (!visible) return;
		this.clearDotFor(visible.suggestion.id);
		this.clicked(visible.suggestion, 'later');
		void this.postOutcome(visible.suggestion.id, {status: 'dismissed', page: this.page});
		this.follow(visible.suggestion);
		this.evaluate();
	}

	/**
	 * Snooze 1 hour / Not for this one / Stop this kind / Suggestion settings. `target` is the
	 * suggestion the menu was opened for: the choice still counts if its card closed meanwhile.
	 */
	menu(choice: CornerMenuChoice, openSettings?: () => void, target?: CornerSuggestion): void {
		const current = this.visible?.suggestion;
		const suggestion = target ?? current;
		if (!suggestion) return;
		if (current?.id === suggestion.id) {
			this.close(choice === 'snooze' ? 'later' : 'x');
		} else {
			// Its card is gone already (closed by itself, or taken down elsewhere).
			this.handled.add(appearanceKey(suggestion));
			Activity.track('suggestion.closed', {
				suggestion_id: suggestion.id,
				kind: suggestion.kind,
				how: choice === 'snooze' ? 'later' : 'x',
			});
		}
		this.clearDotFor(suggestion.id);
		this.clicked(suggestion, `menu:${choice}`);
		const status = choice === 'snooze' ? 'later' : 'dismissed';
		void this.postOutcome(suggestion.id, {status, menu: choice, page: this.page}).then(() => {
			if (choice === 'stop') CornerSettings.refresh();
		});
		if (choice === 'stop') {
			CornerSettings.applyServerChange({kinds: {[suggestion.kind]: false}});
			// Nothing else of that kind this session.
			for (const s of this.suggestions) if (s.kind === suggestion.kind) this.handled.add(appearanceKey(s));
		}
		if (choice === 'settings') openSettings?.();
		if (choice !== 'snooze') this.follow(suggestion);
		this.evaluate();
	}

	/** Report 'used' and do what the main button says. */
	private runAction(suggestion: CornerSuggestion): void {
		const page = this.page;
		void this.postOutcome(suggestion.id, {status: 'used', page, device: currentDevice()});
		const action = suggestion.action;
		if (action.special === 'open_brief' && action.channel_id) {
			RouterUtils.transitionTo(
				action.message_id
					? Routes.dmChannelMessage(action.channel_id, action.message_id)
					: Routes.dmChannel(action.channel_id),
			);
			return;
		}
		if (action.special === 'open_wrap_up' && action.channel_id) {
			// The end-of-day wrap-up: the same card in the Konvo chat.
			RouterUtils.transitionTo(
				action.message_id
					? Routes.dmChannelMessage(action.channel_id, action.message_id)
					: Routes.dmChannel(action.channel_id),
			);
			return;
		}
		if (action.special === 'open_waiting' && action.context?.item_id) {
			// "Waiting on a reply": the conversation itself, with the reply or nudge drafted in its bar.
			const context = action.context;
			WaitingStore.focus(context.item_id);
			void WaitingStore.refresh({force: true});
			const conversationId = context.conversation_id ?? '';
			RouterUtils.transitionTo(
				context.source === 'mail'
					? Routes.dmMail(context.message_id)
					: context.source === 'whatsapp'
						? Routes.dmWhatsAppChat(conversationId)
						: context.guild_id
							? Routes.guildChannel(context.guild_id, conversationId)
							: Routes.dmChannel(conversationId),
			);
			return;
		}
		if (action.special === 'connect') {
			// Not connected: the panel shows its connect step first and keeps the request for afterwards.
			KonvoPanel.open({
				starter: action.starter ?? 'free',
				request: action.request,
				context: action.context,
				source: 'suggestion',
				suggestionId: suggestion.id,
				page,
				run: Boolean(action.request),
			});
			return;
		}
		KonvoPanel.open({
			starter: action.starter ?? 'free',
			request: action.request,
			context: action.context,
			source: 'suggestion',
			suggestionId: suggestion.id,
			page,
		});
	}

	// ---- Ready cards and the list (ReadyActions) ----

	/**
	 * A ready card's button on the pop-up: the card closes (as clicked, `target` is the action kind)
	 * and ReadyActions does the rest. Returns the suggestion that was up.
	 */
	closeForAction(target: string): CornerSuggestion | null {
		const visible = this.close('main');
		if (!visible) return null;
		this.clearDotFor(visible.suggestion.id);
		this.clicked(visible.suggestion, target);
		this.evaluate();
		return visible.suggestion;
	}

	/** Acted on (from the pop-up or the list): report 'used' and never pop it up again here. */
	reportUsed(suggestion: CornerSuggestion, target?: string): void {
		this.forget(suggestion, target);
		void this.postOutcome(suggestion.id, {status: 'used', page: this.page, device: currentDevice()});
		this.evaluate();
	}

	/** ✕ on a list entry: a dismissal, as the pop-up's ✕. */
	reportDismissed(suggestion: CornerSuggestion): void {
		this.forget(suggestion, 'x');
		void this.postOutcome(suggestion.id, {status: 'dismissed', page: this.page});
		this.evaluate();
	}

	/** A list entry's `run` button: what the pop-up's main button did. */
	runFromList(suggestion: CornerSuggestion, target: string | null = 'main'): void {
		this.forget(suggestion, target ?? undefined);
		this.runAction(suggestion);
	}

	/** Done with in the list: no pop-up, dot or second report for it in this tab. */
	private forget(suggestion: CornerSuggestion, target?: string): void {
		for (const s of this.suggestions) if (s.id === suggestion.id) this.handled.add(appearanceKey(s));
		this.handled.add(appearanceKey(suggestion));
		if (this.visible?.suggestion.id === suggestion.id) this.hide();
		this.clearDotFor(suggestion.id);
		if (target) this.clicked(suggestion, target);
	}

	/** The corner icon was clicked with nothing waiting: an empty panel with the field focused. */
	openAsk(): void {
		KonvoPanel.open({starter: 'free', request: '', source: 'corner', page: this.page, run: false});
	}

	/**
	 * Report an outcome. Once this tab ended a suggestion (used, dismissed) nothing more is sent
	 * for it, and a time-out is reported once.
	 */
	private async postOutcome(id: string, outcome: CornerOutcome): Promise<void> {
		if (this.finals.has(id)) return;
		if (isFinalOutcome(outcome)) this.finals.add(id);
		if (outcome.status === 'timed_out') {
			if (this.timedOut.has(id)) return;
			this.timedOut.add(id);
		}
		const requestedAt = Date.now();
		try {
			const response = await http.post<unknown>(cornerOutcomeEndpoint(id), {body: outcome});
			// The fifth ignore in a row: the reply says to ask "Am I getting in your way?".
			AskChangeSignal.report(response?.body, requestedAt);
		} catch (error) {
			logger.warn(`Could not report ${outcome.status} for ${id}`, error);
		}
	}

	// ---- What happened next (suggestion.followed) ----

	private follow(suggestion: CornerSuggestion): void {
		const existing = this.followers.get(suggestion.id);
		if (existing) clearTimeout(existing.timer);
		const now = Date.now();
		const away = !(Window.focused && Window.visible);
		const follower: Follower = {
			id: suggestion.id,
			kind: suggestion.kind,
			starter: suggestion.action.starter,
			startedAt: now,
			panelStarters: [],
			askedKonvo: false,
			areas: new Set(),
			awayMs: 0,
			awaySince: away ? now : null,
			timer: setTimeout(() => this.finishFollow(suggestion.id), this.timings.followMs),
		};
		this.followers.set(suggestion.id, follower);
	}

	private finishFollow(id: string): void {
		const follower = this.followers.get(id);
		if (!follower) return;
		this.followers.delete(id);
		const now = Date.now();
		const awayMs = follower.awayMs + (follower.awaySince != null ? now - follower.awaySince : 0);
		const next = classifyFollowed(follower.kind, follower.starter, {
			panelStarters: follower.panelStarters,
			askedKonvo: follower.askedKonvo,
			areas: [...follower.areas],
			awayMs,
			windowMs: now - follower.startedAt,
		});
		Activity.track('suggestion.followed', {suggestion_id: id, kind: follower.kind, next});
	}

	private watchFollowSignals(): () => void {
		const stops = [
			reaction(
				() => (KonvoPanel.isOpen ? (KonvoPanel.options?.starter ?? 'free') : null),
				(starter) => {
					if (!starter) return;
					for (const f of this.followers.values()) f.panelStarters.push(starter);
				},
			),
			reaction(
				() => AskKonvo.isOpen,
				(open) => {
					if (!open) return;
					for (const f of this.followers.values()) f.askedKonvo = true;
				},
			),
			reaction(
				() => pageForPath(Navigation.pathname),
				(page) => {
					for (const f of this.followers.values()) f.areas.add(page);
				},
			),
			reaction(
				() => Window.focused && Window.visible,
				(active) => {
					const now = Date.now();
					for (const f of this.followers.values()) {
						if (!active && f.awaySince == null) f.awaySince = now;
						if (active && f.awaySince != null) {
							f.awayMs += now - f.awaySince;
							f.awaySince = null;
						}
					}
				},
			),
		];
		return () => {
			for (const stop of stops) stop();
		};
	}

	// ---- Push deep link: ?konvo_panel=<suggestion id> ----

	/** `?konvo_panel=<id>` in the address (a push tap, on boot or while running): note it and drop it. */
	private readDeepLink(): boolean {
		const search = Navigation.search || window.location.search;
		if (!search.includes(CORNER_DEEP_LINK_PARAM)) return false;
		const params = new URLSearchParams(search);
		const id = params.get(CORNER_DEEP_LINK_PARAM);
		if (!id) return false;
		this.deepLinkId = id;
		this.deepLinkAt = Date.now();
		params.delete(CORNER_DEEP_LINK_PARAM);
		const rest = params.toString();
		const path = Navigation.pathname || window.location.pathname;
		RouterUtils.replaceWith(`${path}${rest ? `?${rest}` : ''}${Navigation.hash || window.location.hash}`);
		return true;
	}

	/**
	 * Opened from a push: run that suggestion's action straight away. A link that could not be
	 * run within 30 s (the first load failed) is forgotten rather than opening the panel later
	 * out of nowhere, and a suggestion that is already dealt with is not run again.
	 */
	private runDeepLink(): void {
		const id = this.deepLinkId;
		if (!id) return;
		this.deepLinkId = null;
		if (Date.now() - this.deepLinkAt > DEEP_LINK_TTL_MS) return;
		if (this.finals.has(id)) return;
		const suggestion =
			this.suggestions.find((s) => s.id === id) ?? (this.dot?.suggestion.id === id ? this.dot.suggestion : null);
		if (!suggestion) {
			// Opened from a push after the app was closed: the pop-up is no longer pending, so load it by id.
			const generation = this.generation;
			void http
				.get<{suggestion: CornerSuggestion}>(cornerSuggestionEndpoint(id))
				.then((response) => {
					if (generation !== this.generation) return;
					const loaded = response.body?.suggestion;
					if (!loaded) return this.openAsk();
					if (isFinalStatus(loaded.status) || this.finals.has(loaded.id)) return;
					this.handled.add(appearanceKey(loaded));
					this.clicked(loaded, 'main', 'push');
					this.runAction(loaded);
				})
				.catch(() => {
					if (generation === this.generation) this.openAsk();
				});
			return;
		}
		if (isFinalStatus(suggestion.status)) return;
		this.handled.add(appearanceKey(suggestion));
		if (this.visible?.suggestion.id === id) this.hide();
		this.clearDotFor(id);
		this.clicked(suggestion, 'main', 'push');
		this.runAction(suggestion);
	}
}

export default new CornerStore();
